import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CAPS, DASHES, DEFAULT_FILL, DEFAULT_STROKE, JOINS, lineWidthOf, MARKERS, PATTERNS, symbolOf,
  type Geometry, type LayerStyle,
} from '@/utils/layerStyle';
import { StyleSwatch } from './StyleSwatch';

/**
 * The symbol of a layer, by its geometry (as the common GIS tools offer it): a point's marker, size, turn, fill and
 * outline; a line's colour, width, dash, ends and corners, arrows and casing; an area's fill, pattern and outline. A
 * layer of mixed features (`any`) is offered all three. Colours that come from classes are not offered here.
 */

export const inputClass = 'w-full px-2 py-1 border border-gn-light rounded bg-gn-surface text-gn-primary text-xs';
export const labelClass = 'block text-[11px] text-gn-secondary mb-0.5';

interface Field {
  key: string;
  name: string;
}

interface Props {
  style: LayerStyle;
  geometry: Geometry;
  /** The colour comes from classes (categories, graduated): no colour of its own. */
  classed: boolean;
  /** Sized by a value (proportional): no size of its own. */
  sized: boolean;
  clusters: boolean;
  numberFields: Field[];
  set: (patch: Partial<LayerStyle>) => void;
}

export const Slider: React.FC<{ label: string; value: number; min: number; max: number; step?: number; onChange: (v: number) => void }> =
  ({ label: text, value, min, max, step, onChange }) => (
    <label className="block">
      <span className={labelClass}>{text}</span>
      <input type="range" min={min} max={max} step={step ?? 1} value={value} className="w-full" onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );

const Colour: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label: text, value, onChange }) => (
  <label className="block">
    <span className={labelClass}>{text}</span>
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-7 w-full" aria-label={text} />
  </label>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="space-y-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gn-muted">{title}</p>
    {children}
  </div>
);

export const SymbolControls: React.FC<Props> = ({ style, geometry, classed, sized, clusters, numberFields, set }) => {
  const { t } = useTranslation();
  const fill = style.fill ?? DEFAULT_FILL;
  const stroke = style.stroke ?? DEFAULT_STROKE;
  const any = geometry !== 'point' && geometry !== 'line' && geometry !== 'area';
  const points = geometry === 'point' || any;
  const lines = geometry === 'line' || any;
  const areas = geometry === 'area' || any;
  const opacity = style.fillOpacity ?? (geometry === 'point' ? 0.9 : geometry === 'line' ? 1 : 0.55);

  const choice = (current: string, options: readonly string[], key: string, onPick: (v: string) => void, render?: (v: string) => React.ReactNode) => (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)} title={t(`styles.${key}_${o.replace('-', '_')}`)}
                aria-pressed={current === o}
                className={`flex items-center gap-1 px-1.5 py-1 rounded border text-[10px] ${current === o ? 'border-gn-blue bg-gn-tertiary' : 'border-gn-light'}`}>
          {render ? render(o) : t(`styles.${key}_${o.replace('-', '_')}`)}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      {!classed && (
        <div className="grid grid-cols-2 gap-2">
          <Colour label={lines && !areas && !points ? t('styles.line_colour') : t('styles.fill')} value={fill} onChange={(v) => set({ fill: v })} />
          <Slider label={t('styles.opacity', { value: Math.round(opacity * 100) })} value={Math.round(opacity * 100)} min={5} max={100}
                  onChange={(v) => set({ fillOpacity: v / 100 })} />
        </div>
      )}
      {classed && (
        <Slider label={t('styles.opacity', { value: Math.round(opacity * 100) })} value={Math.round(opacity * 100)} min={5} max={100}
                onChange={(v) => set({ fillOpacity: v / 100 })} />
      )}

      {points && (
        <Section title={any ? t('styles.for_points') : t('styles.marker')}>
          {choice(style.marker ?? 'circle', MARKERS, 'marker', (m) => set({ marker: m }),
            (m) => <StyleSwatch symbol={{ ...symbolOf(style, 'point', fill, 12), marker: m, rotation: 0 }} width={18} height={16} />)}
          <div className="grid grid-cols-2 gap-2">
            {!sized && <Slider label={t('styles.size', { value: style.size ?? 8 })} value={style.size ?? 8} min={2} max={48} onChange={(v) => set({ size: v })} />}
            <div>
              <span className={labelClass}>{t('styles.outline')}</span>
              <div className="flex gap-1">
                <input type="color" value={stroke} onChange={(e) => set({ stroke: e.target.value })} className="h-7 w-10" aria-label={t('styles.outline')} />
                <input type="number" min={0} max={20} step={0.5} className={inputClass} value={style.strokeWidth ?? 1} title={t('styles.outline_width')}
                       onChange={(e) => set({ strokeWidth: Math.max(0, Math.min(20, Number(e.target.value))) })} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Slider label={t('styles.rotation', { value: style.rotation ?? 0 })} value={style.rotation ?? 0} min={0} max={359}
                    onChange={(v) => set({ rotation: v || null })} />
            <label className="block">
              <span className={labelClass}>{t('styles.rotate_by')}</span>
              <select className={inputClass} value={style.rotateBy ?? ''} onChange={(e) => set({ rotateBy: e.target.value || null })}>
                <option value="">{t('styles.rotate_none')}</option>
                {numberFields.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
              </select>
            </label>
          </div>
          {clusters && (
            <>
              <label className="flex items-center gap-1 text-[11px]">
                <input type="checkbox" checked={!!style.cluster} onChange={(e) => set({ cluster: e.target.checked })} />
                {t('styles.cluster')}
              </label>
              {style.cluster && (
                <Slider label={t('styles.cluster_distance', { value: style.clusterDistance ?? 40 })} value={style.clusterDistance ?? 40}
                        min={5} max={200} onChange={(v) => set({ clusterDistance: v })} />
              )}
            </>
          )}
        </Section>
      )}

      {lines && (
        <Section title={any ? t('styles.for_lines') : t('styles.line')}>
          {!sized && (
            <Slider label={t('styles.line_width', { value: lineWidthOf(style) })} value={lineWidthOf(style)} min={0.5} max={20} step={0.5}
                    onChange={(v) => set({ lineWidth: v })} />
          )}
          <div>
            <span className={labelClass}>{t('styles.dash')}</span>
            {choice(style.dash ?? 'solid', DASHES, 'dash', (d) => set({ dash: d, lineWidth: lineWidthOf(style) }),
              (d) => <StyleSwatch symbol={{ ...symbolOf(style, 'line', fill, 2), dash: d, casing: null, arrows: false }} width={30} height={10} />)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className={labelClass}>{t('styles.line_cap')}</span>
              <select className={inputClass} value={style.lineCap ?? 'butt'} onChange={(e) => set({ lineCap: e.target.value, lineWidth: lineWidthOf(style) })}>
                {CAPS.map((c) => <option key={c} value={c}>{t(`styles.cap_${c}`)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={labelClass}>{t('styles.line_join')}</span>
              <select className={inputClass} value={style.lineJoin ?? 'round'} onChange={(e) => set({ lineJoin: e.target.value, lineWidth: lineWidthOf(style) })}>
                {JOINS.map((j) => <option key={j} value={j}>{t(`styles.join_${j}`)}</option>)}
              </select>
            </label>
          </div>
          <label className="flex items-center gap-1 text-[11px]">
            <input type="checkbox" checked={!!style.arrows} onChange={(e) => set({ arrows: e.target.checked, lineWidth: lineWidthOf(style) })} />
            {t('styles.arrows')}
          </label>
          {!sized && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-[11px]">
                <input type="checkbox" checked={!!style.casing}
                       onChange={(e) => set({ casing: e.target.checked ? '#111827' : null, lineWidth: lineWidthOf(style) })} />
                {t('styles.casing')}
              </label>
              {style.casing && (
                <>
                  <input type="color" value={style.casing} onChange={(e) => set({ casing: e.target.value })} className="h-6 w-8" aria-label={t('styles.casing')} />
                  <input type="number" min={0.5} max={10} step={0.5} className={`${inputClass} w-16`} value={style.casingWidth ?? 1}
                         title={t('styles.casing_width')} onChange={(e) => set({ casingWidth: Math.max(0, Math.min(10, Number(e.target.value))) })} />
                </>
              )}
            </div>
          )}
        </Section>
      )}

      {areas && (
        <Section title={any ? t('styles.for_areas') : t('styles.area')}>
          <label className="flex items-center gap-1 text-[11px]">
            <input type="checkbox" checked={!!style.outlineOnly} onChange={(e) => set({ outlineOnly: e.target.checked || null })} />
            {t('styles.outline_only')}
          </label>
          {!style.outlineOnly && (
            <div>
              <span className={labelClass}>{t('styles.pattern')}</span>
              {choice(style.pattern ?? 'solid', PATTERNS, 'pattern', (p) => set({ pattern: p === 'solid' ? null : p }),
                (p) => <StyleSwatch symbol={{ ...symbolOf(style, 'area', fill), pattern: p, outlineOnly: false }} width={26} height={16} />)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className={labelClass}>{t('styles.outline')}</span>
              <div className="flex gap-1">
                <input type="color" value={stroke} onChange={(e) => set({ stroke: e.target.value })} className="h-7 w-10" aria-label={t('styles.outline')} />
                <input type="number" min={0} max={20} step={0.5} className={inputClass} value={style.strokeWidth ?? 1} title={t('styles.outline_width')}
                       onChange={(e) => set({ strokeWidth: Math.max(0, Math.min(20, Number(e.target.value))) })} />
              </div>
            </div>
            <label className="block">
              <span className={labelClass}>{t('styles.outline_dash')}</span>
              <select className={inputClass} value={style.dash ?? 'solid'} onChange={(e) => set({ dash: e.target.value === 'solid' ? null : e.target.value })}>
                {DASHES.map((d) => <option key={d} value={d}>{t(`styles.dash_${d.replace('-', '_')}`)}</option>)}
              </select>
            </label>
          </div>
        </Section>
      )}
    </div>
  );
};

export default SymbolControls;
